import { Component, inject, OnInit, output, signal } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { FeatureService } from 'src/app/core/firebase/feature.service';
import { FreePlayLobbyService } from 'src/app/core/firebase/free-play.service';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { EditTournamentComponent } from 'src/app/game/component/edit-tournament/edit-tournament.component';
import { DynamicContentService } from 'src/app/shared/services/dynamic-content/dynamic-content.service';
import { SopoTransitionService } from 'src/app/shared/services/sopo-transition/sopo-transition.service';
import { ZendeskService } from 'src/app/shared/services/zendesk/zendesk.service';
import { SopoConfirmSettings } from 'src/app/sopo-popup/interfaces/confirm';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { BOTTOM_MENU_ITEMS, MAIN_MENU_ITEMS, MenuItem } from './interfaces/sopo-menu';
import { isElectron, isMacOS } from 'src/app/shared/utils/platform.utils';
import { environment } from 'src/environments/environment';
import AppDetailsElectron from 'src/app/plugins/app-details.plugin';

@Component({
    selector: 'app-sopo-menu',
    templateUrl: './sopo-menu.component.html',
    styleUrls: ['./sopo-menu.component.scss'],
    host: {
        '[class.close]': 'closing()',
        '(click)': 'closeMenu()',
    },
    standalone: true,
})
export class SopoMenuComponent implements OnInit {
    private _dynamicContentService = inject(DynamicContentService);
    private _router = inject(Router);
    private _navCtrl = inject(NavController);
    private _authDispatcher = inject(AuthStoreDispatcher);
    private _transitionService = inject(SopoTransitionService);
    private _sopoPopupService = inject(SopoPopupService);
    private _freeplayService = inject(FreePlayLobbyService);
    private _featureService = inject(FeatureService);
    private _zendeskService = inject(ZendeskService);
    private _activatedRoute = inject(ActivatedRoute);

    protected isDesktop = signal(this._featureService.isDesktop);
    protected closing = signal(false);
    protected isMacOS = signal(isMacOS());
    protected appVersion = signal('');
    protected isStaging = signal(environment.staging);

    close = output();

    mainMenuItems = MAIN_MENU_ITEMS;
    bottomMenuItems = BOTTOM_MENU_ITEMS;

    constructor() { }

    ngOnInit() {
        this.init();
    }

    isItemVisible(item: MenuItem): boolean {
        if (!item.showOnlyOn) {
            return true;
        }
        if (item.showOnlyOn === 'desktop') {
            return this.isDesktop();
        }
        if (item.showOnlyOn === 'web') {
            return !this.isDesktop();
        }
        return true;
    }

    onMenuItemClick(item: MenuItem) {
        if (this.closing()) return;
        this.closeMenu();
        switch (item.action.type) {
            case 'navigation':
                this.handleNavigation(item.action.url);
                break;
            case 'operation':
                this.handleOperation(item.action.actionName);
                break;
        }
    }

    closeMenu() {
        this.closing.set(true);
    }

    onAnimationEnd(event: AnimationEvent) {
        if (event.animationName.includes('slide-out')) {
            this.close.emit();
        }
    }

    private async handleNavigation(url: string) {
        if (!url) {
            console.warn('Invalid URL.');
            return;
        }

        const answer = await this.confirmNavigation();
        if(!answer) return;
        this._dynamicContentService.closeCertificatePreview();
        const isAnonymous = await this._authDispatcher.isAnonymous();
        if (this._router.url.includes('/social') || this._router.url.includes('/points') || (this._router.url.includes('/lobby') && !this._router.url.includes('/sweepstakes') ) || this._router.url.includes('/rewards') || (isAnonymous && this._router.url.includes('/login')) || this._router.url.includes('/shop-home') || this._router.url.includes('/verification')) {
            let navigationExtras: NavigationExtras;
            if (this._router.url.includes('/cashier') && this._activatedRoute.snapshot.queryParams?.tabs === 'limited') {
                navigationExtras = {
                    queryParams: {
                        tabs: 'limited',
                    },
                };
            }
            this._navCtrl.navigateForward(url, navigationExtras);
        } else {
            await this._transitionService.openTransition();
            this._navCtrl.navigateRoot(url);
        }
    }

    private handleOperation(actionName: string) {
        switch (actionName) {
            case 'logout':
                this._sopoPopupService.clearViewContainer('app');
                const dialogOptions: SopoConfirmSettings = {
                    data: {
                        header: 'Are You Sure You Want To Log Out?',
                        okText: 'Confirm',
                        cancelText: 'Cancel',
                    }
                }
                this._sopoPopupService.promptForConfirmation('app', dialogOptions).then((logout) => {
                    if (logout) this._authDispatcher.logout();
                })
                break;
            case 'support':
                this._zendeskService.openSupportChat();
                break;
            default:
                console.warn('Unknown operation:', actionName);
        }
    }

    private async confirmNavigation(): Promise<boolean> {
        const url = this._router.url;
        if (url.includes('tournament-registration') && url.includes('SOPO')) {
            if (this._sopoPopupService.getOpenedDialogs('app') > 0 && this._sopoPopupService.isDialogOpen('app', EditTournamentComponent)) return await this._sopoPopupService
                .promptForConfirmation('app', {
                    data: {
                        body: `You have unsaved changes. Do you want to leave this page?`,
                        okText: `Yes`,
                        cancelText: 'No'
                    }
                })
        }
        if (url.includes('/tournament-queue')) {
            const confirmation = await this._freeplayService.navigateAwayFromJackpotQueue();
            if (!confirmation) return;
        }
        return true;
    }

    private async init(): Promise<void> {
        if (!isElectron()) return;

        const appInfo = await AppDetailsElectron.getAppInfo();
        if (!appInfo.version) return;

        this.appVersion.update(() => 'v' + appInfo.version);
    }
}
